/**
 * Helper to determine if a node is an inline element.
 */
const isInlineNode = (node) => {
    if (node.type === 'text') return true;
    if (node.type === 'element') {
        const inlineTags = ['span', 'b', 'strong', 'i', 'em', 'u', 'a', 'br'];
        return inlineTags.includes(node.tagName);
    }
    return false;
};

/**
 * Recursively normalizes the JSON tree according to rules.
 */
const normalizeJsonTree = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) return [];

    let cleaned = [];
    for (let node of nodes) {
        if (node.type === 'text') {
            cleaned.push(node);
        } else if (node.type === 'element') {
            if (node.children) {
                node.children = normalizeJsonTree(node.children);
            }
            
            // Skip removing empty elements if they are naturally self-closing
            if (node.tagName !== 'br' && node.tagName !== 'hr' && node.tagName !== 'img') {
                const meaningfulChildren = (node.children || []).filter(c => 
                    !(c.type === 'text' && c.content.replace(/\u00A0/g, '_').trim() === '')
                );
                
                if (meaningfulChildren.length === 0) {
                    continue; // Skip tags that are completely empty
                }
            }
            cleaned.push(node);
        }
    }

    let merged = cleaned;

    // Remove completely empty text nodes at the end
    while (merged.length > 0) {
        const last = merged[merged.length - 1];
        if (last.type === 'text' && last.content.replace(/\u00A0/g, '_').trim() === '') {
            merged.pop();
        } else {
            break;
        }
    }

    return merged;
};

/**
 * Wraps root-level inline nodes in a <p> element.
 */
const wrapRootNodes = (nodes) => {
    let finalArray = [];
    let currentParagraph = null;

    for (let node of nodes) {
        if (isInlineNode(node)) {
            if (!currentParagraph) {
                currentParagraph = { type: 'element', tagName: 'p', children: [] };
                finalArray.push(currentParagraph);
            }
            currentParagraph.children.push(node);
        } else {
            currentParagraph = null; // Reset paragraph block
            finalArray.push(node);
        }
    }
    return finalArray;
};

/**
 * Converts a raw HTML string into a JSON string representing the DOM tree.
 */
export const convertHtmlToJson = (html) => {
    if (!html) return '[]';

    // Check if it's already a JSON string (could happen if double processed)
    if (html.trim().startsWith('[') && html.trim().endsWith(']')) {
        try {
            JSON.parse(html);
            return html;
        } catch(e) {
            // Not a valid JSON, proceed to parse as HTML
        }
    }

    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    const parseNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return { type: 'text', content: node.textContent };
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const obj = { type: 'element', tagName: node.tagName.toLowerCase() };
            
            // Capture safe attributes (added href and target for links)
            const attrs = {};
            for (let i = 0; i < node.attributes.length; i++) {
                const attr = node.attributes[i];
                if (['style', 'align', 'dir', 'color', 'size', 'face', 'href', 'target'].includes(attr.name)) {
                    attrs[attr.name] = attr.value;
                }
            }
            if (Object.keys(attrs).length > 0) {
                obj.attributes = attrs;
            }
            
            const children = Array.from(node.childNodes).map(parseNode).filter(Boolean);
            if (children.length > 0) {
                obj.children = children;
            }
            
            return obj;
        }
        return null;
    };
    
    const rawJsonArray = Array.from(temp.childNodes).map(parseNode).filter(Boolean);
    const normalizedArray = normalizeJsonTree(rawJsonArray);
    const finalArray = wrapRootNodes(normalizedArray);

    // Replace non-breaking spaces with standard spaces after the tree is fully built and filtered
    return JSON.stringify(finalArray).replace(/\u00A0/g, ' ');
};

/**
 * Converts a JSON string back into an HTML string.
 * Falls back to returning the original string if it is not valid JSON.
 */
export const convertJsonToHtml = (jsonStr) => {
    if (!jsonStr) return '';
    
    try {
        const jsonArray = JSON.parse(jsonStr);
        
        if (!Array.isArray(jsonArray)) return jsonStr;
        
        const buildNode = (obj) => {
            if (!obj) return null; // Safe fallback
            if (obj.type === 'text') {
                return document.createTextNode(obj.content || '');
            }
            // If it's explicitly an element, or malformed legacy object without type
            if (obj.type === 'element' || !obj.type) {
                const tagName = obj.tagName || 'span'; // Safe fallback
                const el = document.createElement(tagName);
                if (obj.attributes) {
                    Object.entries(obj.attributes).forEach(([key, value]) => {
                        el.setAttribute(key, value);
                    });
                }
                if (obj.children && Array.isArray(obj.children)) {
                    obj.children.forEach(childObj => {
                        const childEl = buildNode(childObj);
                        if (childEl) el.appendChild(childEl);
                    });
                }
                return el;
            }
            return null;
        };
        
        const temp = document.createElement('div');
        jsonArray.forEach(obj => {
            const el = buildNode(obj);
            if (el) temp.appendChild(el);
        });
        
        return temp.innerHTML;
    } catch (e) {
        // Fallback for old HTML data that is not JSON
        return jsonStr;
    }
};
